/**
 * The context a reporter should never have to type.
 *
 * Support's first two questions are always "which page?" and "what did the
 * console say?", and a reporter who has to answer them writes a worse report or
 * gives up. The browser already knows, so it is collected silently and sent
 * with the opening message.
 *
 * Errors are buffered from the moment the app mounts because the interesting
 * ones happen long before anyone thinks to report them.
 */

/** Kept small on purpose: this rides along in the message body. */
const MAX_BUFFERED_ERRORS = 5;
const MAX_ERROR_LENGTH = 300;

const recentErrors: string[] = [];
let isListening = false;

const remember = (message: string): void => {
	recentErrors.push(message.slice(0, MAX_ERROR_LENGTH));
	if (recentErrors.length > MAX_BUFFERED_ERRORS) recentErrors.shift();
};

/** Idempotent: mounting the provider twice must not double every error. */
export const startErrorBuffer = (): void => {
	if (isListening) return;
	isListening = true;
	window.addEventListener("error", (event) => {
		remember(event.message || String(event.error));
	});
	window.addEventListener("unhandledrejection", (event) => {
		remember(`Unhandled rejection: ${String(event.reason)}`);
	});
};

/**
 * A compact block appended to the opening message. Plain text rather than a
 * new column: it is read by a human in the thread, and a schema for it is a
 * migration that has to earn itself first.
 */
export const describeReportContext = (): string => {
	const lines = [
		`Page: ${window.location.href}`,
		// innerWidth/innerHeight is the viewport — the area the screenshot
		// actually covers. Calling it the screen sent whoever reads it looking
		// for a monitor that size.
		`Viewport: ${window.innerWidth}×${window.innerHeight} at ${window.devicePixelRatio}x`,
		`Browser: ${navigator.userAgent}`,
		`Language: ${navigator.language}`,
		`Time: ${new Date().toISOString()}`,
	];
	if (recentErrors.length > 0) {
		lines.push("", "Recent browser errors:");
		recentErrors.forEach((error) => lines.push(`  • ${error}`));
	}
	return lines.join("\n");
};

/** First line of what the reporter wrote, so they need not also invent a title. */
export const deriveTitle = (message: string): string => {
	const firstLine = message.trim().split("\n")[0].trim();
	if (firstLine.length <= 80) return firstLine;
	return `${firstLine.slice(0, 77)}…`;
};
